import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  type ChartOptions,
  type Plugin,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { observer } from 'mobx-react-lite';
import { useCapacityStore } from '../store/CapacityPlannerContext';
import styles from './cpHeadcountBreakdown.module.scss';

ChartJS.register(ArcElement, Tooltip, Legend);

// ---- constants --------------------------------------------------------------

const COLORS = {
  PA: '#522d6e',
  MO: '#34b53a',
  QI: '#f59e0b',
  Fixed: '#94a3b8',
} as const;

// ---- center-text plugin (scoped, not global) --------------------------------

function buildCenterPlugin(total: number): Plugin<'doughnut'> {
  return {
    id: 'cpDoughnutCenter',
    afterDraw(chart) {
      const { ctx, chartArea } = chart;
      if (!chartArea) return;
      const cx = (chartArea.left + chartArea.right) / 2;
      const cy = (chartArea.top + chartArea.bottom) / 2;
      ctx.save();
      ctx.font = 'bold 22px Inter, sans-serif';
      ctx.fillStyle = '#1e293b';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(Math.round(total)), cx, cy - 8);
      ctx.font = '11px Inter, sans-serif';
      ctx.fillStyle = '#64748b';
      ctx.fillText('Total HC', cx, cy + 14);
      ctx.restore();
    },
  };
}

// ---- props ------------------------------------------------------------------

interface CPHeadcountBreakdownProps {
  selectedMonth: string;
}

// ---- component --------------------------------------------------------------

const CPHeadcountBreakdown = observer(({ selectedMonth }: CPHeadcountBreakdownProps) => {
  const store = useCapacityStore();
  const { dashboardData } = store;

  if (!dashboardData) {
    return (
      <div className={styles.container}>
        <div className={styles.title}>
          <i className="pi pi-chart-pie" />
          HC Breakdown
        </div>
        <div className={styles.chartWrap} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: '#94a3b8', fontSize: 13 }}>No data available</span>
        </div>
      </div>
    );
  }

  const hb = dashboardData.hc_breakdown[selectedMonth];
  const paVal  = hb?.pa              ?? 0;
  const moVal  = hb?.mo              ?? 0;
  const qiVal  = hb?.qi              ?? 0;
  const aggVal = hb?.aggregation     ?? 0;
  const total  = paVal + moVal + qiVal + aggVal;

  const chartData = {
    labels: ['PA', 'MO', 'QI', 'Agg/Fixed'],
    datasets: [
      {
        data: [paVal, moVal, qiVal, aggVal],
        backgroundColor: [COLORS.PA, COLORS.MO, COLORS.QI, COLORS.Fixed],
        borderWidth: 2,
        borderColor: '#ffffff',
        hoverBorderColor: '#ffffff',
      },
    ],
  };

  const centerPlugin = buildCenterPlugin(total);

  const chartOptions: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.label}: ${ctx.parsed.toFixed(1)} FTE`,
        },
      },
    },
  };

  const legendItems = [
    { key: 'PA',  color: COLORS.PA,    value: paVal  },
    { key: 'MO',  color: COLORS.MO,    value: moVal  },
    { key: 'QI',  color: COLORS.QI,    value: qiVal  },
    { key: 'Agg', color: COLORS.Fixed, value: aggVal },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        <i className="pi pi-chart-pie" />
        HC Breakdown
        <span className={styles.monthLabel}>{selectedMonth}</span>
      </div>
      <div className={styles.chartWrap}>
        <Doughnut data={chartData} options={chartOptions} plugins={[centerPlugin]} />
      </div>
      <div className={styles.legend}>
        {legendItems.map((item) => (
          <div key={item.key} className={styles.legendItem}>
            <span className={styles.legendDot} style={{ backgroundColor: item.color }} />
            <span className={styles.legendLabel}>{item.key}</span>
            <span className={styles.legendValue}>{item.value.toFixed(1)} FTE</span>
          </div>
        ))}
      </div>
    </div>
  );
});

export default CPHeadcountBreakdown;
